import { Typography } from '@mui/material'
import { QuickTradeProto, QuoteType, quoteTypeToJSON } from '../../../../proto/generated/quick-trade'
import { useEffect, useMemo, useState } from 'react'
import { getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { OrderType, TransactionType } from '../../../../proto/generated/order'
import { useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../../../selectors/home'
import StrategyLegsViewer from '../../../strategyTemplate/StrategyLegsViewer'

type Props = {
    quickTrade: QuickTradeProto
    dontShowStrategyLegs?: boolean
}

const QuickTradeTooltip = ({ quickTrade, dontShowStrategyLegs = false }: Props) => {
    const { primary } = useSelector(selectPrimarySecurity)
    const computedSecurity = useMemo(()=>{
        return quickTrade?.security?.code ? quickTrade?.security : primary
    }, [primary, quickTrade])
    const secKey = useMemo(()=>getSecurityKey(computedSecurity?.exchange??0, computedSecurity?.code??''), [computedSecurity])
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (secKey) getSecurityAsync(secKey).then(setSecurity) }, [secKey])

    const transactionType = useMemo(()=>{
        return quickTrade.templateOrderExecutionParameters.transactionType ? quickTrade.templateOrderExecutionParameters.transactionType : undefined
    }, [quickTrade.templateOrderExecutionParameters.transactionType])

    const quantityRender = useMemo(()=>{
        const params = quickTrade?.templateOrderExecutionParameters
        if (params?.amount) {
            return `Amt: ${params.amount}`
        } else if (params?.lots) {
            return `${params.lots} ${params.lots>1 ? 'Lots' : 'Lot'}`
        } else if (params?.quantity) {
            return `${params.quantity}`
        } else if (params?.multiplier) {
            return `${params.multiplier} Multiplier`
        }
        return ''
    }, [quickTrade])

    const orderTypeRender = useMemo(()=>{
        const params = quickTrade?.templateOrderExecutionParameters
        if(params.orderType===OrderType.MARKET_ORDER) {
            return 'Market'
        } else if(params.orderType===OrderType.LIMIT_ORDER) {
            let limitData = ''
            switch(params.quoteType) {
                case QuoteType.BIPS:
                    let sign = params.quoteValue>0 ? 1 : -1
                    if(params?.transactionType) {
                        if(params.transactionType===TransactionType.BUY) {
                            sign = -1 * sign
                        }
                    }
                    limitData = `${sign>0 ? '+' : '-'}${Math.abs(params.quoteValue)} BIP${[0,1].includes(Math.abs(params.quoteValue)) ? '' : 'S'}`
                    break
                case QuoteType.TICKS:
                    limitData = `${params.quoteValue>0 ? '+' : ''}${params.quoteValue} TICK${[0,1].includes(Math.abs(params.quoteValue)) ? '' : 'S'}`
                    break
                case QuoteType.LEVEL:
                    limitData = `${params.quoteValue>0 ? '+' : ''}${params.quoteValue} LEVEL${[0,1].includes(Math.abs(params.quoteValue)) ? '' : 'S'}`
                    break
            }
            return `Limit: ${limitData}`
        }
        return ''
    }, [quickTrade])

    return quickTrade.templateOrderExecutionParameters.transactionType ? (
        <div className='font-feature-num' style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Typography
                component={'span'}
                className={`
                        ${transactionType === TransactionType.BUY ? 'buy-button' : 'sell-button'}
                    `}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '5px' }}
            >
                {transactionType === TransactionType.BUY ? 'B' : 'S'}
            </Typography>
            <Typography component={'span'} variant='caption' sx={{ color: theme=>theme.palette.text.primary, textWrap: 'nowrap'}}>
                {security?.symbol} × {quantityRender} @{orderTypeRender}
            </Typography>
        </div>
    ) : (
        <div className='font-feature-num' style={{ minWidth: dontShowStrategyLegs ? 0 : '400px'}}>
            <Typography variant='caption' sx={{color: theme=>theme.palette.text.primary, lineHeight: '20px'}}>{security?.symbol} @{orderTypeRender} {quickTrade?.templateOrderExecutionParameters?.multiplier ? ` | Multiplier: ${quickTrade?.templateOrderExecutionParameters?.multiplier}` : `| Amount: ${quickTrade?.templateOrderExecutionParameters?.amount}`}</Typography>
            {/* <Typography variant='caption' sx={{ display: 'block', color: theme=>theme.palette.text.primary}}>Description: {quickTrade.description}</Typography> */}
            {
                dontShowStrategyLegs ? null :
                quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs ?
                <StrategyLegsViewer strategyLegs={quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs} /> : null
            }
        </div>
    )
}

export default QuickTradeTooltip